/**
 * Poll arithmetic (protocol polls.ts, 2026-10-29), without the store: the
 * reducer and the screens share it.
 */
import type { Message, MessagePollVoteData, Poll, PollAnswerCount } from '@neosocial/protocol';

/** Every answer's count (a missing one is 0), in the poll's order. */
export function pollCounts(p: Poll): PollAnswerCount[] {
  const got = new Map((p.results?.answer_counts ?? []).map((c) => [c.id, c]));
  return p.answers.map(
    (a) => got.get(a.answer_id) ?? { id: a.answer_id, count: 0, me_voted: false },
  );
}

/** Whether the poll is over: final, or past its end (the server finalizes it shortly). */
export function pollOver(p: Poll, now = Date.now()): boolean {
  return !!p.results?.is_finalized || (!!p.expiry && Date.parse(p.expiry) <= now);
}

/**
 * One vote event on a message. Your own (`mine`) is skipped when the count
 * already shows it (you voted from this window and it was applied at once).
 */
export function applyPollVote(
  m: Message,
  d: Pick<MessagePollVoteData, 'answer_id'>,
  mine: boolean,
  add: boolean,
): Message {
  const p = m.poll;
  if (!p) return m;
  const list = pollCounts(p);
  const i = list.findIndex((c) => c.id === d.answer_id);
  if (i < 0) return m;
  const c = list[i]!;
  if (mine && c.me_voted === add) return m;
  list[i] = {
    ...c,
    count: Math.max(0, c.count + (add ? 1 : -1)),
    me_voted: mine ? add : c.me_voted,
  };
  return {
    ...m,
    poll: { ...p, results: { is_finalized: !!p.results?.is_finalized, answer_counts: list } },
  };
}

/** A MESSAGE_UPDATE's poll, with the reader's own votes it can't know about kept. */
export function keepMyVotes(prev: Message, next: Partial<Message>): Partial<Message> {
  if (!next.poll?.results || !prev.poll) return next;
  const mine = new Set(
    pollCounts(prev.poll)
      .filter((c) => c.me_voted)
      .map((c) => c.id),
  );
  if (!mine.size) return next;
  return {
    ...next,
    poll: {
      ...next.poll,
      results: {
        ...next.poll.results,
        answer_counts: next.poll.results.answer_counts.map((c) => ({
          ...c,
          me_voted: c.me_voted || mine.has(c.id),
        })),
      },
    },
  };
}
